import { useCallback, useEffect, useState } from 'react';
import {
  Plug, CheckCircle2, AlertCircle, Loader2, Link2, Unlink,
  RefreshCw, Clock,
} from 'lucide-react';
import { integrazioni } from '../utils/api';

// Metadati di stato della connessione (allineati alla macchina a stati backend).
const STATO_META = {
  not_connected: { label: 'Non connessa', color: 'var(--ds-muted)' },
  connecting:    { label: 'Connessione in corso', color: 'var(--zone-blue)' },
  active:        { label: 'Connessa', color: 'var(--ok)' },
  error:         { label: 'Errore di connessione', color: 'var(--ds-danger)' },
  expired:       { label: 'Sessione scaduta', color: 'var(--warn)' },
  disconnected:  { label: 'Disconnessa', color: 'var(--ds-muted)' },
};

const PROVIDER_FIC = 'fatture_in_cloud';

function Pill({ colore, children, icona }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontSize: 12, fontWeight: 500, color: colore,
      background: `color-mix(in srgb, ${colore} 12%, transparent)`,
      border: `1px solid color-mix(in srgb, ${colore} 35%, transparent)`,
      borderRadius: 999, padding: '4px 12px', whiteSpace: 'nowrap',
    }}>
      {icona || <span style={{ width: 7, height: 7, borderRadius: '50%', background: colore }} />}
      {children}
    </span>
  );
}

/** Lo stato di una riga: connessione reale se collegabile, «In arrivo» altrimenti. */
function StatoCella({ servizio }) {
  if (servizio.stato_servizio !== 'disponibile') {
    return <Pill colore="var(--ds-muted)" icona={<Clock size={12} />}>In arrivo</Pill>;
  }
  const stato = servizio.connessione?.stato || 'not_connected';
  const meta = STATO_META[stato] || STATO_META.not_connected;
  return <Pill colore={meta.color}>{meta.label}</Pill>;
}

/**
 * L'azione di una riga.
 *
 * Un servizio non ancora collegabile mostra un bottone DISABILITATO con il
 * motivo accanto, mai un bottone che sembra funzionare: è il difetto che
 * FE-070 ha chiuso il 18/08 (stub che fingevano successo).
 */
function AzioneCella({ servizio, busy, onCollega, onScollega }) {
  if (servizio.stato_servizio !== 'disponibile') {
    return (
      <button type="button" disabled style={disabledBtn} title={servizio.motivo}>
        In arrivo
      </button>
    );
  }

  const stato = servizio.connessione?.stato || 'not_connected';
  const isActive = stato === 'active';
  const isErrore = stato === 'error' || stato === 'expired';

  if (isActive) {
    return (
      <button onClick={onScollega} disabled={busy} style={secondaryBtn}>
        <Unlink size={16} /> Scollega
      </button>
    );
  }
  return (
    <button onClick={onCollega} disabled={busy} style={primaryBtn}>
      {busy ? <Loader2 size={16} className="spin" /> : (isErrore ? <RefreshCw size={16} /> : <Link2 size={16} />)}
      {isErrore ? 'Riprova' : 'Collega'}
    </button>
  );
}

export default function Integrazioni() {
  const [servizi, setServizi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null); // { tipo: 'success'|'error', testo }

  const carica = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await integrazioni.catalogo();
      setServizi(res.data.servizi || []);
    } catch (e) {
      setError('Impossibile caricare le integrazioni. Riprova.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Esito del ritorno dal flusso OAuth (?fic=connected|error&reason=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const esito = params.get('fic');
    if (esito === 'connected') {
      setFlash({ tipo: 'success', testo: 'Account Fatture in Cloud collegato con successo.' });
    } else if (esito === 'error') {
      const reason = params.get('reason');
      const testo = reason === 'state'
        ? 'Sessione di collegamento non valida o scaduta. Riprova.'
        : reason === 'denied'
          ? 'Autorizzazione negata su Fatture in Cloud.'
          : 'Collegamento non riuscito. Riprova tra poco.';
      setFlash({ tipo: 'error', testo });
    }
    if (esito) {
      // pulizia della query dalla URL (senza reload)
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const collega = async () => {
    try {
      setBusy(true);
      setFlash(null);
      const res = await integrazioni.ficStart();
      window.location.href = res.data.authorize_url; // redirect a Fatture in Cloud
    } catch (e) {
      setBusy(false);
      const status = e?.response?.status;
      const testo = status === 503
        ? 'Integrazione non ancora configurata sul server. Riprova più tardi.'
        : status === 403
          ? 'Integrazione non disponibile nel tuo piano.'
          : 'Impossibile avviare il collegamento. Riprova.';
      setFlash({ tipo: 'error', testo });
    }
  };

  const scollega = async () => {
    if (!window.confirm('Vuoi scollegare Fatture in Cloud? Dovrai autorizzare di nuovo per riemettere e-fatture.')) return;
    try {
      setBusy(true);
      await integrazioni.ficDisconnect();
      setFlash({ tipo: 'success', testo: 'Fatture in Cloud scollegata.' });
      await carica();
    } catch (e) {
      setFlash({ tipo: 'error', testo: 'Impossibile scollegare. Riprova.' });
    } finally {
      setBusy(false);
    }
  };

  const azioniPer = (s) => (s.chiave === 'fatture_in_cloud'
    ? { onCollega: collega, onScollega: scollega }
    : { onCollega: undefined, onScollega: undefined });

  return (
    <div style={{ flex: 1, padding: '28px 24px', maxWidth: 900, margin: '0 auto', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <Plug size={22} color="var(--zone-slate)" strokeWidth={1.8} />
        <h1 className="font-display" style={{ fontSize: 24, fontWeight: 700, color: 'var(--ink)', margin: 0 }}>
          Integrazioni
        </h1>
      </div>
      <p style={{ fontSize: 14, color: 'var(--ds-muted)', margin: '0 0 24px', lineHeight: 1.6 }}>
        Collega facilmente i tuoi strumenti esterni a mAIstro.
      </p>

      {flash && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18,
          padding: '12px 16px', borderRadius: 'var(--radius-base)', fontSize: 13,
          color: flash.tipo === 'success' ? 'var(--ok)' : 'var(--ds-danger)',
          background: `color-mix(in srgb, ${flash.tipo === 'success' ? 'var(--ok)' : 'var(--ds-danger)'} 10%, transparent)`,
          border: `1px solid color-mix(in srgb, ${flash.tipo === 'success' ? 'var(--ok)' : 'var(--ds-danger)'} 30%, transparent)`,
        }}>
          {flash.tipo === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{flash.testo}</span>
        </div>
      )}

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 24, color: 'var(--ds-muted)', fontSize: 14 }}>
          <Loader2 size={16} className="spin" /> Caricamento…
        </div>
      )}

      {!loading && error && (
        <div style={{
          padding: '20px 22px', borderRadius: 'var(--radius-card)', background: 'var(--chip)',
          border: '1px solid var(--ds-border)', color: 'var(--ds-danger)', fontSize: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <span>{error}</span>
          <button onClick={carica} style={secondaryBtn}><RefreshCw size={15} /> Riprova</button>
        </div>
      )}

      {/* Empty state: non dovrebbe accadere (il catalogo è costante), ma una
          lista vuota senza spiegazione è uno schermo bianco. */}
      {!loading && !error && servizi.length === 0 && (
        <div style={{ ...cardStyle, color: 'var(--ds-muted)', fontSize: 14 }}>
          Nessuna integrazione disponibile al momento.
        </div>
      )}

      {!loading && !error && servizi.length > 0 && (
        <div style={{ border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-card)', overflow: 'hidden', background: 'var(--ds-bg)' }}>
          {/* Intestazione: solo da tablet in su. Su mobile ogni riga si legge
              da sola e le etichette di colonna diventerebbero rumore. */}
          <div className="integrazioni-head" style={headRow}>
            <div>Servizio</div>
            <div>Descrizione</div>
            <div>Stato</div>
            <div style={{ textAlign: 'right' }}>Collega</div>
          </div>

          {servizi.map((s, i) => {
            const { onCollega, onScollega } = azioniPer(s);
            return (
              <div key={s.chiave} className="integrazioni-riga" style={{ ...bodyRow, borderTop: i === 0 ? 'none' : '1px solid var(--ds-border)' }}>
                <div className="font-display" style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>
                  {s.nome}
                </div>
                <div style={{ fontSize: 13, color: 'var(--ds-muted)', lineHeight: 1.5 }}>
                  {s.descrizione}
                  {s.stato_servizio !== 'disponibile' && s.motivo && (
                    <div style={{ marginTop: 4, color: 'var(--ds-muted)', fontSize: 12.5 }}>{s.motivo}</div>
                  )}
                  {s.connessione?.stato === 'active' && s.connessione?.company_id && (
                    <div style={{ marginTop: 4, fontSize: 12.5 }}>
                      Azienda collegata: <strong style={{ color: 'var(--ds-text)' }}>{s.connessione.company_id}</strong>
                    </div>
                  )}
                  {['error', 'expired'].includes(s.connessione?.stato) && s.connessione?.ultimo_errore && (
                    <div style={{ marginTop: 4, fontSize: 12.5, color: 'var(--ds-danger)' }}>
                      Dettaglio: {s.connessione.ultimo_errore}
                    </div>
                  )}
                </div>
                <div><StatoCella servizio={s} /></div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <AzioneCella servizio={s} busy={busy} onCollega={onCollega} onScollega={onScollega} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p style={{ fontSize: 13, color: 'var(--ds-muted)', margin: '18px 0 0', textAlign: 'center' }}>
        Altri servizi in arrivo. Ti avviseremo appena saranno disponibili.
      </p>

      <style>{`
        .integrazioni-head { display: none; }
        .integrazioni-riga { display: grid; grid-template-columns: 1fr; gap: 10px; }
        @media (min-width: 760px) {
          .integrazioni-head,
          .integrazioni-riga {
            display: grid;
            grid-template-columns: 1.1fr 2fr auto auto;
            gap: 16px;
            align-items: center;
          }
        }
      `}</style>
    </div>
  );
}

const headRow = {
  padding: '12px 20px', background: 'var(--chip)',
  borderBottom: '1px solid var(--ds-border)',
  fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)',
  textTransform: 'uppercase', letterSpacing: '.04em',
};

const bodyRow = { padding: '18px 20px' };

const cardStyle = {
  background: 'var(--ds-bg)', border: '1px solid var(--ds-border)',
  borderRadius: 'var(--radius-card)', padding: '22px 24px',
  boxShadow: '0 1px 2px rgba(0,0,0,.04)',
};

const btnBase = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  minHeight: 44, padding: '10px 20px', borderRadius: 'var(--radius-base)',
  fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap',
};

const primaryBtn = { ...btnBase, border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer' };

const secondaryBtn = {
  ...btnBase, padding: '10px 18px', border: '1px solid var(--ds-border)',
  background: 'var(--chip)', color: 'var(--ds-text)', cursor: 'pointer',
};

const disabledBtn = {
  ...btnBase, padding: '10px 18px', border: '1px dashed var(--ds-border)',
  background: 'transparent', color: 'var(--ds-muted)', cursor: 'not-allowed',
};
